// Auto-hiding scrollbars: the themed thumb (see `--scrollbar-thumb` in style.css)
// is transparent at rest and only shown while an element is actively being
// scrolled, fading back out after a short idle delay. The scrollbar's 8px channel
// stays reserved the whole time (only the thumb's colour toggles), so showing and
// hiding never shifts content.
//
// Driven by a single capture-phase `scroll` listener on the window — scroll events
// don't bubble, but capturing catches them from every scroll container. The scrolled
// element gets a `scrollbar-active` class, removed after HIDE_DELAY ms of no further
// scrolling on that element.

/** How long after the last scroll event the thumb stays visible (ms). */
export const SCROLLBAR_HIDE_DELAY = 1100;

/** Marker class CSS keys off to reveal the thumb; see style.css. */
export const SCROLLBAR_ACTIVE_CLASS = 'scrollbar-active';

/** Per-element idle timers, keyed weakly so detached nodes are collected. */
const timers = new WeakMap<Element, ReturnType<typeof setTimeout>>();

/** Resolve the scrolled element from a scroll event. Document/root/body scrolls
 *  report the document (or its scrollingElement) as target — normalise those to
 *  `<html>`, which is what carries the page scrollbar. Returns null for anything
 *  that isn't an Element (so callers can safely skip it). */
export function scrolledElementOf(target: EventTarget | null): Element | null {
    if (target instanceof Element) return target;
    if (target === document || target === (typeof window !== 'undefined' ? window : null)) {
        return document.documentElement;
    }
    return null;
}

/** Mark `el` as actively scrolling and (re)arm its idle timer. Exposed for tests. */
export function markScrolling(el: Element, delay: number = SCROLLBAR_HIDE_DELAY): void {
    el.classList.add(SCROLLBAR_ACTIVE_CLASS);
    const prev = timers.get(el);
    if (prev != null) clearTimeout(prev);
    timers.set(el, setTimeout(() => {
        el.classList.remove(SCROLLBAR_ACTIVE_CLASS);
        timers.delete(el);
    }, delay));
}

let installed = false;

/** Install the global scroll listener once. Safe to call in non-DOM environments
 *  (no-ops) and idempotent. */
export function installScrollbarAutohide(): void {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    window.addEventListener(
        'scroll',
        (e) => {
            const el = scrolledElementOf(e.target);
            if (el != null) markScrolling(el);
        },
        { capture: true, passive: true },
    );
}
